import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Dropdown } from "Components/Shared/Inputs/Dropdown";
import { useRelationTypeValueList } from "Hooks/Options/useRelationTypeValueList";
import { RelationType } from "Infrastructure/Api/Api";
import {
  OTHER_ROLES,
  getRoleCategoryForRelationType,
} from "Models/BusinessRelationTypes";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const CATEGORY_RESOURCES = Resources.Form.RoleCategory;
const ADD_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Add;

type Category = "statutoryBody" | "proxyHolder" | "other" | "beneficialOwner";

type Props = {
  value: RelationType | null;
  onChange: (relationType: RelationType | null) => void;
};

export const AddRelationTypeSelector = ({ value, onChange }: Props) => {
  const { t } = useResource();
  const otherRoles = useRelationTypeValueList({
    allowedRelations: OTHER_ROLES,
  });
  const [isOtherPending, setIsOtherPending] = useState(false);
  const category = value
    ? value === RelationType.BeneficialOwner
      ? "beneficialOwner"
      : (getRoleCategoryForRelationType(value) ?? "")
    : isOtherPending
      ? "other"
      : "";

  const handleCategoryChange = (next: Category) => {
    setIsOtherPending(next === "other");
    if (next === "statutoryBody") {
      onChange(RelationType.StatutoryBody);
      return;
    }
    if (next === "proxyHolder") {
      onChange(RelationType.ProxyHolder);
      return;
    }
    if (next === "beneficialOwner") {
      onChange(RelationType.BeneficialOwner);
      return;
    }
    // "other" — wait until a specific role is picked from the dropdown
    onChange(null);
  };

  return (
    <FormControl>
      <FormLabel
        sx={{
          fontSize: 14,
          fontWeight: 400,
          mb: 1,
          color: "text.primary",
          "&.Mui-focused": { color: "text.primary" },
        }}
      >
        {t(ADD_RESOURCES.RelationTypeLabel)}
      </FormLabel>
      <RadioGroup
        value={category}
        onChange={(_, value) => handleCategoryChange(value as Category)}
      >
        <FormControlLabel
          value="statutoryBody"
          control={<Radio />}
          label={
            <>
              {t(CATEGORY_RESOURCES.StatutoryBody)}
              <Typography variant="body2" color="text.secondary">
                {t(CATEGORY_RESOURCES.StatutoryBodyHint)}
              </Typography>
            </>
          }
        />
        <FormControlLabel
          value="proxyHolder"
          control={<Radio />}
          label={t(CATEGORY_RESOURCES.ProxyHolder)}
        />
        <FormControlLabel
          value="beneficialOwner"
          control={<Radio />}
          label={t(ADD_RESOURCES.BeneficialOwnerOption)}
        />
        <FormControlLabel
          value="other"
          control={<Radio />}
          label={t(CATEGORY_RESOURCES.Other)}
        />
      </RadioGroup>
      {category === "other" && (
        <Dropdown
          name="specificRole"
          label={t(CATEGORY_RESOURCES.SpecificRole)}
          value={value && isOtherRole(value) ? value : ""}
          options={otherRoles.map(({ code, name }) => ({
            Value: code,
            Name: name,
          }))}
          onChange={(selected) => onChange(selected as RelationType)}
        />
      )}
    </FormControl>
  );
};

const isOtherRole = (value: RelationType | null) =>
  !!value && OTHER_ROLES.includes(value);
